import produce from 'immer';
import zip from 'lodash/zip';

import {MetricType} from 'common/query-builder/query-block/constants';
import {ALL_MATH_TYPES, ChartType, PropertyType, ResourceType, ValueRepresentationType} from 'common/report/constants';
import {
  AnalysisType,
  ChartPlotStyle,
  FilterClause,
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsResourceType,
} from 'common/report/insights/util/types';
import type {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {queryModule} from 'common/report/report-manager/query';
import {flattenSeries} from 'common/report/insights/util';
import {SerializedCohort} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {FiltersOperator, SelectorTypeDefinitions} from 'common/query-builder/query-entry-section/types';
import {AVATAR_PROPERTIES, NAME_PROPERTIES} from 'common/util/profiles';
import {fetchUsers} from 'common/report/queries/query-api/users-client';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {User} from 'common/report/queries/query-api/types';
import {formatUserProperties} from 'common/util/format-property';
import {extractUserSummaryProperties} from 'common/report/util/profile/utils';
import {MPProperties} from 'common/constants';

import {NumberOperator, StringOperator} from '../property-filter-menu/models/types';
import {TimeUnit} from '../date-range-picker/models/types';
import {RecordingMetadata} from './types';

export const REPLAY_RETENTION_DAYS = 30;

export const REPLAY_EVENT = `$mp_session_record`;
export const REPLAY_LENGTH_PROPERTY = `session_length_ms`;
export const REPLAY_START_TIME_PROPERTY = `replay_start_time`;

export const REPLAY_ID_PROPERTY = `$mp_replay_id`;

const REPLAY_LENGTH_MS_PROPERTY = `replay_length_ms`;

export const RECORDING_METADATA_GROUPS = [
  {
    value: MPProperties.DistinctId,
    resourceType: InsightsResourceType.Events,
    propertyType: PropertyType.String,
  },
  {
    value: REPLAY_ID_PROPERTY,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
  {
    value: REPLAY_START_TIME_PROPERTY,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
];

export const ALL_RECORDINGS_EVENTS_GROUPS = [
  {
    value: REPLAY_ID_PROPERTY,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
  {
    value: `$time`,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.Datetime,
    typeCast: PropertyType.Number,
    unit: null,
  },
  {
    value: `$event_name`,
    resourceType: InsightsResourceType.Events,
    search: ``,
    dataGroupId: null,
    propertyType: PropertyType.String,
    typeCast: null,
    unit: null,
  },
];

export const REPLAY_ID_SET_FILTER: FilterClause = {
  dataset: `$mixpanel`,
  value: REPLAY_ID_PROPERTY,
  resourceType: InsightsResourceType.Events,
  profileType: null,
  search: ``,
  dataGroupId: null,
  filterType: PropertyType.String,
  defaultType: PropertyType.String,
  filterOperator: StringOperator.Set,
  filterValue: null,
  propertyObjectKey: null,
};

// Filter out replays that are less than 2 seconds long
const REPLAY_LENGTH_FILTER: FilterClause = {
  dataset: `$mixpanel`,
  value: REPLAY_LENGTH_MS_PROPERTY,
  resourceType: InsightsResourceType.Events,
  profileType: null,
  search: ``,
  dataGroupId: null,
  filterType: PropertyType.Number,
  defaultType: PropertyType.Number,
  filterOperator: NumberOperator.AtLeast,
  filterValue: 2_000,
  propertyObjectKey: null,
};

/**
 * Base query to fetch replay events, to be used as a starting point for further filtering.
 * We determine which event to pick by doing a max on the replay event duration to indicate how long the replay lasted and segment by replay start time to get when the replay started.
 *
 * NOTES:
 * - when querying replays from within the report:
 *    - we should modify the date range to be the report's date range minus 24 hours to account for max session length
 * - if we need to get the length of the replay:
 *    - modify the date range to be the reports date range plus 24 hours
 */
const BASE_REPLAY_EVENT_QUERY: InsightsBookmarkParams = {
  sorting: {},
  columnWidths: {bar: {}},
  displayOptions: {
    analysis: AnalysisType.Linear,
    chartType: ChartType.Table,
    plotStyle: ChartPlotStyle.Standard,
    statSigControl: [],
    value: ValueRepresentationType.Absolute,
  },
  sections: {
    cohorts: [],
    filter: [REPLAY_LENGTH_FILTER],
    formula: [],
    group: RECORDING_METADATA_GROUPS,
    show: [
      {
        behavior: {
          type: MetricType.Event,
          resourceType: InsightsResourceType.Events,
          dataGroupId: null,
          filters: [],
          name: REPLAY_EVENT,
          profileType: null,
        },
        measurement: {
          math: ALL_MATH_TYPES.MAX,
          perUserAggregation: null,
          rolling: null,
          cumulative: false,
          property: {
            dataset: `$mixpanel`,
            defaultType: PropertyType.Number,
            name: REPLAY_LENGTH_MS_PROPERTY,
            resourceType: InsightsResourceType.Events,
            type: PropertyType.Number,
            unit: null,
          },
        },
      },
    ],
    time: [
      {
        unit: TimeUnit.Day,
        value: 30,
      },
    ],
  },
};

export function getReportReplaysDisabledMessage(timestampMs: number): string | null {
  const retentionMs = REPLAY_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  if (new Date().getTime() - timestampMs > retentionMs) {
    return `Replay data is only retained for the last ${REPLAY_RETENTION_DAYS} days.`;
  }
  return null;
}

export async function queryReplays(params: InsightsBookmarkParams, limit = 100): Promise<InsightsQueryResults> {
  const body = {
    bookmark: params,
    use_query_cache: true,
    limit,
  };

  const queryParams = {
    ...(getWorkspaceId() ? {workspace_id: getWorkspaceId()} : {}),
    project_id: getProjectId(),
  };

  const response = await queryModule.executeReportQuery({
    endpoint: `insights`,
    body,
    queryParams,
    location: {
      search: window.location.search,
      hash: window.location.hash,
      pathname: window.location.pathname,
      key: ``,
      state: ``,
    },
  });
  // TODO: handle error
  const results: InsightsQueryResults = await response.json();

  return results;
}

async function queryUsers(distinctIds: string[]): Promise<User[]> {
  const rawCohort: SerializedCohort = {
    groups: [
      {
        type: SelectorTypeDefinitions.CohortGroup,
        event: {
          resourceType: ResourceType.Cohort,
          value: `$all_users`,
          label: `All Users`,
        },
        filters: [
          {
            resourceType: ResourceType.User,
            propertyName: `$distinct_id`,
            propertyObjectKey: null,
            propertyDefaultType: PropertyType.String,
            propertyType: PropertyType.String,
            filterOperator: StringOperator.Equals,
            filterValue: distinctIds,
          } as PropertyFilter,
        ],
        filtersOperator: FiltersOperator.And,
        behavioralFiltersOperator: FiltersOperator.And,
        groupingOperator: null,
        property: null,
      },
    ],
  };

  const results = await fetchUsers({
    cohort: Cohort.parseCohort(rawCohort),
    includeAllUsers: false,
    columns: [MPProperties.LastSeen, ...AVATAR_PROPERTIES, ...NAME_PROPERTIES].map((value) => ({
      value,
      resourceType: ResourceType.User,
    })),
  });

  return results;
}

function getLabeledSegmentValues(segmentValues: string[], groups = RECORDING_METADATA_GROUPS): Record<string, string> {
  // ignore show clause
  return zip(segmentValues.slice(1), groups).reduce((acc, [segment, group]) => {
    acc[group.value] = segment;
    return acc;
  }, {});
}

function flattenSeriesWithoutMetadata(
  results: InsightsQueryResults,
  groups = RECORDING_METADATA_GROUPS,
): Array<[string[], Record<string, number>]> {
  return flattenSeries(results.series).filter(([path]) => {
    // skip all the "overall" segments
    return path.length === groups.length + 1 && path[groups.length] !== `$overall`;
  });
}

export async function fetchEventsForReplays(replays: RecordingMetadata[]): Promise<RecordingMetadata[]> {
  const replayIds = replays.map((replay) => replay.replayId);
  const distinctIds = replays.map((replay) => replay.distinctId);

  const params = produce(BASE_REPLAY_EVENT_QUERY, (draft) => {
    draft.sections.show = [
      {
        behavior: {
          type: MetricType.Event,
          resourceType: InsightsResourceType.Events,
          dataGroupId: null,
          filters: [],
          name: `$all_events`,
          profileType: null,
        },
        measurement: {
          math: ALL_MATH_TYPES.TOTAL,
          cumulative: false,
          perUserAggregation: null,
          property: null,
          rolling: null,
        },
      },
    ];
    draft.sections.filter = [
      {
        dataset: `$mixpanel`,
        value: REPLAY_ID_PROPERTY,
        resourceType: InsightsResourceType.Events,
        profileType: null,
        search: ``,
        dataGroupId: null,
        filterType: PropertyType.String,
        defaultType: PropertyType.String,
        filterOperator: StringOperator.Equals,
        filterValue: replayIds,
        propertyObjectKey: null,
      },
      {
        dataset: `$mixpanel`,
        value: `$distinct_id`,
        // use the events resource type, otherwise we don't include anon users.
        resourceType: InsightsResourceType.Events,
        profileType: null,
        search: ``,
        dataGroupId: null,
        filterType: PropertyType.String,
        defaultType: PropertyType.String,
        filterOperator: StringOperator.Equals,
        filterValue: distinctIds,
        propertyObjectKey: null,
      },
    ];
    draft.sections.group = ALL_RECORDINGS_EVENTS_GROUPS;
  });

  const eventResults = await queryReplays(params, 2000);

  const res = flattenSeriesWithoutMetadata(eventResults, ALL_RECORDINGS_EVENTS_GROUPS).map(([path, metric]) => {
    const labeledSegmentValues = getLabeledSegmentValues(path, ALL_RECORDINGS_EVENTS_GROUPS);
    const quantity = Object.values(metric)[0];

    return {
      replayId: labeledSegmentValues[REPLAY_ID_PROPERTY],
      eventName: labeledSegmentValues[`$event_name`],
      eventTime: Number(labeledSegmentValues[`$time`].replaceAll(`,`, ``)),
      quantity,
    };
  });

  return replays.map((replay) => ({
    ...replay,
    replayEvents: res
      .filter((eventGroup) => eventGroup.replayId === replay.replayId)
      .map((eventGroup) => ({
        eventName: eventGroup.eventName,
        eventTime: eventGroup.eventTime,
        quantity: eventGroup.quantity,
      })),
  }));
}

export async function fetchRecordingsByIds(replayIds: string[], distinctIds: string[]): Promise<RecordingMetadata[]> {
  if (!replayIds?.length || !distinctIds?.length) {
    return [];
  }

  const params = produce(BASE_REPLAY_EVENT_QUERY, (draft) => {
    draft.sections.filter = [
      ...draft.sections.filter,
      {
        dataset: `$mixpanel`,
        value: REPLAY_ID_PROPERTY,
        resourceType: InsightsResourceType.Events,
        profileType: null,
        search: ``,
        dataGroupId: null,
        filterType: PropertyType.String,
        defaultType: PropertyType.String,
        filterOperator: StringOperator.Equals,
        filterValue: replayIds,
        propertyObjectKey: null,
      },
    ];
  });

  const [replayResults, userResults] = await Promise.all([queryReplays(params), queryUsers(distinctIds)]);

  const userSummaryByDistinctId = userResults.reduce((acc, user) => {
    const formattedProperties = formatUserProperties(user.$properties, {});
    acc.set(user.$distinct_id, extractUserSummaryProperties(formattedProperties));
    return acc;
  }, new Map());

  // fill in any users without profiles with default values
  for (const distinctId of distinctIds) {
    if (!userSummaryByDistinctId.has(distinctId)) {
      userSummaryByDistinctId.set(distinctId, extractUserSummaryProperties(new Map()));
    }
  }

  const flatSeries = flattenSeriesWithoutMetadata(replayResults);

  return flatSeries.map(([path, metric]) => {
    const labeledSegmentValues = getLabeledSegmentValues(path);
    const duration = Object.values(metric)[0];
    const distinctId = labeledSegmentValues[MPProperties.DistinctId];

    const userSummaryProperties = userSummaryByDistinctId.get(distinctId) ?? {};
    return {
      time: Number(labeledSegmentValues[REPLAY_START_TIME_PROPERTY]),
      distinctId,
      email: userSummaryProperties.email,
      name: userSummaryProperties.name,
      replayId: labeledSegmentValues[REPLAY_ID_PROPERTY],
      avatarUrl: userSummaryProperties.avatarUrl,
      duration,
    };
  });
}

export async function fetchRecordingsForDistinctId(distinctId: string): Promise<RecordingMetadata[]> {
  const params = produce(BASE_REPLAY_EVENT_QUERY, (draft) => {
    draft.sections.filter = [
      ...draft.sections.filter,
      {
        dataset: `$mixpanel`,
        value: `$distinct_id`,
        // use the events resource type, otherwise we don't include anon users.
        resourceType: InsightsResourceType.Events,
        profileType: null,
        search: ``,
        dataGroupId: null,
        filterType: PropertyType.String,
        defaultType: PropertyType.String,
        filterOperator: StringOperator.Equals,
        filterValue: [distinctId],
        propertyObjectKey: null,
      },
    ];
  });

  const [replayResults, userResults] = await Promise.all([queryReplays(params), queryUsers([distinctId])]);

  // some strange transformations, but important to stay consistent with users page / profile app
  const formattedUserProps = formatUserProperties(userResults[0]?.$properties ?? {}, {});
  const userSummaryProperties = extractUserSummaryProperties(formattedUserProps);

  return flattenSeriesWithoutMetadata(replayResults).map(([path, metric]) => {
    const labeledSegmentValues = getLabeledSegmentValues(path);
    const duration = Object.values(metric)[0];

    return {
      time: Number(labeledSegmentValues[REPLAY_START_TIME_PROPERTY]),
      distinctId,
      email: userSummaryProperties?.email,
      name: userSummaryProperties?.name,
      replayId: labeledSegmentValues[REPLAY_ID_PROPERTY],
      avatarUrl: userSummaryProperties?.avatarUrl,
      duration,
    };
  });
}
